/**
 * UnifiedDashboardsTiles — Tiles view for the unified Dashboards page.
 *
 * Two sections on one page: "My dashboards" (user dashboards) and
 * "Prebuilt dashboards" (curated templates). A compact empty-state is shown
 * when the user has no personal dashboards, and the Prebuilt section is
 * always kept visible. Handlers/permissions come from the orchestrator ctx
 * (same ones the old tabs used).
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
    Database, Eye, Lock, MoreVertical, Pencil, Plus, Trash2, Layers, Sparkles, Loader2, LayoutDashboard,
} from "lucide-react";
import ReadinessBadge from "@/components/dashboards/ReadinessBadge";
import { IconPlate } from "./MyDashboardsTab";
import { TemplateMoreMenu } from "./UnifiedDashboardsTable";

export function SectionHeader({ title, count, testId }) {
    return (
        <div className="flex items-center gap-3 mb-3" data-testid={testId}>
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-foreground whitespace-nowrap">{title}</h3>
            <span className="inline-flex items-center justify-center h-5 min-w-[1.5rem] px-1.5 rounded-md bg-secondary text-[11px] font-mono text-muted-foreground border border-border"
                data-testid={`${testId}-count`}>{count}</span>
            <div className="flex-1 h-px bg-border" />
        </div>
    );
}

const chip = "inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-mono font-semibold uppercase tracking-wider";

function StatusChip({ kind }) {
    const map = {
        live: ["Live ready", "bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300"],
        demo: ["Demo mode", "bg-primary/10 border-primary/30 text-primary"],
        needs: ["Needs data", "bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300"],
        instantiated: ["Instantiated", "bg-[hsl(var(--success))]/10 border-[hsl(var(--success))]/30 text-[hsl(var(--success))]"],
        template: ["Template", "bg-secondary/60 border-border text-muted-foreground"],
    };
    const [label, tone] = map[kind] || map.template;
    return <span className={`${chip} ${tone}`}>{label}</span>;
}

function ModelBadges({ models = [], counts = {}, prefix = "model", testId }) {
    if (!models.length) return null;
    return (
        <div className="flex flex-wrap gap-1" data-testid={testId}>
            {models.map((m) => (
                <Badge key={m} variant="outline" className="font-mono text-[10px] font-normal border-primary/25 bg-primary/[0.06] text-primary rounded-md">
                    <span className="opacity-70 mr-0.5">{prefix}:</span>{m}
                    {counts[m] != null && <span className="opacity-60 ml-1">· {Number(counts[m]).toLocaleString()} rows</span>}
                </Badge>
            ))}
        </div>
    );
}

function CornerIcons({ readiness, testId, title }) {
    return (
        <div className="flex items-center gap-1.5 shrink-0">
            {readiness && <ReadinessBadge readiness={readiness} compact data-testid={testId} />}
            <div className="h-7 w-7 rounded-md border border-border bg-secondary/40 flex items-center justify-center text-muted-foreground" title={title}>
                <Database className="h-3.5 w-3.5" />
            </div>
        </div>
    );
}

const cardCls = "group relative border border-border bg-card rounded-xl p-4 cursor-pointer shadow-sm hover:shadow-md hover:border-foreground/15 hover:-translate-y-0.5 transition-all duration-200 flex flex-col";

function MyDashboardCard({ d, ctx }) {
    const { canWriteData, canWriteDashboard, openDashboard, setTileSourceTarget, del } = ctx;
    const canEdit = d.can_edit !== undefined ? d.can_edit : canWriteDashboard;
    const canDelete = d.can_delete !== undefined ? d.can_delete : canWriteDashboard;
    const hasSources = Boolean((d.attached_sources?.length || 0) || (d.bind_to_models?.length || 0));
    const isPrebuilt = d.source_gallery_template_kind === "prebuilt_dashboard";
    const showConnect = canWriteData && d.needs_source;
    const showAddSource = canWriteData && !d.needs_source && hasSources;
    const hasMenu = showConnect || showAddSource || canDelete;
    return (
        <div className={cardCls} onClick={() => openDashboard(d)} data-testid={`my-dashboard-tile-${d.id}`}>
            <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <StatusChip kind={d.needs_source ? "needs" : "live"} />
                    {isPrebuilt && (
                        <Badge variant="outline" className="font-mono text-[9px] border-primary/30 bg-primary/10 text-primary gap-0.5" data-testid={`my-dashboard-prebuilt-badge-${d.id}`}>
                            <Sparkles className="h-2.5 w-2.5" /> PREBUILT
                        </Badge>
                    )}
                    {d.created_via === "sense_ai_builder" && !d.source_gallery_template_id && (
                        <Badge variant="outline" className="font-mono text-[9px] border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 gap-0.5"
                            title={d.builder_prompt ? `Generated from prompt: "${d.builder_prompt}"` : "Generated by the Sense AI Builder"}
                            data-testid={`my-dashboard-builder-badge-${d.id}`}>
                            <Sparkles className="h-2.5 w-2.5" /> SENSE AI
                        </Badge>
                    )}
                    {d.is_unified && (
                        <Badge variant="outline" className="font-mono text-[9px] border-primary/30 bg-primary/10 text-primary gap-0.5" data-testid={`my-dashboard-unified-${d.id}`}>
                            <Layers className="h-2.5 w-2.5" /> UNIFIED{d.source_count > 1 ? ` · ${d.source_count}` : ""}
                        </Badge>
                    )}
                </div>
                <CornerIcons readiness={d.readiness} testId={`my-dashboard-readiness-${d.id}`} title={d.source_label || "Data source"} />
            </div>
            <div className="flex items-start gap-3 flex-1 mb-3">
                <IconPlate name={d.name} category={d.category} sourceLabel={d.source_label} />
                <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-semibold leading-snug text-foreground line-clamp-2">{d.name}</div>
                    {d.description && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{d.description}</div>}
                    <div className="mt-2">
                        {(d.bind_to_models || []).length > 0 ? (
                            <ModelBadges models={d.bind_to_models} testId={`my-dashboard-bindings-${d.id}`} />
                        ) : d.source_label && d.source_label !== "—" ? (
                            <Badge variant="outline" className="font-mono text-[10px] font-normal border-border bg-secondary/50 text-muted-foreground rounded-md max-w-full truncate" title={d.source_label}>
                                <span className="opacity-70 mr-0.5">source:</span>{d.source_label}
                            </Badge>
                        ) : null}
                    </div>
                </div>
            </div>
            <div className="pt-3 border-t border-border/60 flex items-center justify-between gap-1.5">
                <div className="flex flex-col">
                    <span className="font-mono text-base font-semibold text-foreground leading-none">{d.widgets?.length || 0}</span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">Widgets</span>
                </div>
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    {canEdit && (
                        <Button variant="ghost" size="sm" className="rounded-md h-8 px-2.5 text-xs gap-1 text-muted-foreground hover:text-foreground"
                            onClick={() => openDashboard(d)} title="Open in the editor — rename, add widgets and sources"
                            data-testid={`my-dashboard-edit-${d.id}`}>
                            <Pencil className="h-3.5 w-3.5" /> Edit
                        </Button>
                    )}
                    <Button size="sm" className="rounded-md h-8 px-3 text-xs shadow-sm" onClick={() => openDashboard(d)}
                        data-testid={`my-dashboard-open-${d.id}`}>
                        Open
                    </Button>
                    {hasMenu && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="rounded-md h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                    data-testid={`my-dashboard-more-${d.id}`} title="More actions">
                                    <MoreVertical className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                {showConnect && (
                                    <DropdownMenuItem onClick={() => setTileSourceTarget({ id: d.id, name: d.name })} data-testid={`my-dashboard-tile-connect-data-${d.id}`}>
                                        <Database className="h-3.5 w-3.5 mr-2" /> Connect data
                                    </DropdownMenuItem>
                                )}
                                {showAddSource && (
                                    <DropdownMenuItem onClick={() => setTileSourceTarget({ id: d.id, name: d.name })} data-testid={`my-dashboard-tile-add-source-${d.id}`}>
                                        <Plus className="h-3.5 w-3.5 mr-2" /> Add source
                                    </DropdownMenuItem>
                                )}
                                {canDelete && (showConnect || showAddSource) && <DropdownMenuSeparator />}
                                {canDelete && (
                                    <DropdownMenuItem onClick={() => del(d.id, d.name)} className="text-destructive focus:text-destructive"
                                        data-testid={`my-dashboard-delete-${d.id}`}>
                                        <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete dashboard
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>
        </div>
    );
}

function TemplateCard({ t, ctx }) {
    const { nav, clone, prebuiltLocked, openUpgrade, onConnectData } = ctx;
    const isModelBound = t._model_bound;
    const isInstantiated = !!t.instance;
    const isDemo = isModelBound && t.demo_mode;
    const status = !isModelBound ? "template" : isDemo ? "demo" : isInstantiated ? "instantiated" : "live";
    const preview = () => nav(`/dashboards/preview/${t.id}`);
    return (
        <div className={cardCls} onClick={preview} title="Click to preview this template with sample data" data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <StatusChip kind={status} />
                    {t.is_unified && (
                        <Badge variant="outline" className="font-mono text-[9px] border-primary/30 bg-primary/10 text-primary gap-0.5" data-testid={`template-unified-${t.id}`}>
                            <Layers className="h-2.5 w-2.5" /> UNIFIED
                        </Badge>
                    )}
                    {t.created_via === "sense_ai_builder" && (
                        <Badge variant="outline" className="font-mono text-[9px] border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 gap-0.5"
                            title={t.builder_prompt ? `Generated from prompt: "${t.builder_prompt}"` : "Generated by the Sense AI Builder"}
                            data-testid={`template-builder-badge-${t.id}`}>
                            <Sparkles className="h-2.5 w-2.5" /> SENSE AI
                        </Badge>
                    )}
                </div>
                <CornerIcons readiness={t.readiness} testId={`template-readiness-${t.id}`} title={isModelBound ? "Model-bound template" : "Custom template"} />
            </div>
            <div className="mb-2.5"><IconPlate name={t.name} category={t.category} /></div>
            <div className="mb-3 flex-1">
                <div className="text-[15px] font-semibold leading-snug text-foreground line-clamp-2">{t.name}</div>
                {t.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-3">{t.description}</div>}
            </div>
            {isModelBound && (t.bind_to_models || []).length > 0 && (
                <div className="mb-3">
                    <ModelBadges models={t.bind_to_models} counts={t.bound_counts || {}} prefix={isDemo ? "sample" : "model"} testId={`template-bindings-${t.id}`} />
                </div>
            )}
            <div className="pt-3 border-t border-border/60 flex items-center justify-between gap-1.5">
                <div className="flex flex-col">
                    <span className="font-mono text-base font-semibold text-foreground leading-none">{t.widgets?.length || 0}</span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">Widgets</span>
                </div>
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="rounded-md h-8 px-2.5 text-xs gap-1 text-muted-foreground hover:text-foreground"
                        onClick={preview} title="Preview this template with sample data" data-testid={`template-preview-${t.id}`}>
                        <Eye className="h-3.5 w-3.5" /> Preview
                    </Button>
                    {prebuiltLocked && !isInstantiated ? (
                        <Button variant="outline" size="sm" className="rounded-md h-8 px-2.5 text-xs gap-1" onClick={() => openUpgrade?.()}
                            title="Prebuilt dashboards aren't included in your plan." data-testid={`template-clone-${t.id}-locked`}>
                            <Lock className="h-3 w-3" /> Upgrade
                        </Button>
                    ) : (
                        <Button size="sm" className="rounded-md h-8 px-3 text-xs shadow-sm" onClick={() => clone(t)} data-testid={`template-clone-${t.id}`}>
                            {isInstantiated ? "Open" : "Use"}
                        </Button>
                    )}
                    <TemplateMoreMenu t={t} ctx={ctx} onConnectData={onConnectData} />
                </div>
            </div>
        </div>
    );
}

export function CompactEmpty({ children, action, testId }) {
    return (
        <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 rounded-xl border border-dashed border-border bg-secondary/20 text-sm text-muted-foreground" data-testid={testId}>
            <span className="flex items-center gap-2"><LayoutDashboard className="h-4 w-4 opacity-50" />{children}</span>
            {action}
        </div>
    );
}

export default function UnifiedDashboardsTiles({ ctx, showMy, showPrebuilt, myRows, myTotal, prebuiltRows, prebuiltTotal, myPager, prebuiltPager, loading, hasAnyDashboards, onCreate }) {
    return (
        <div className="space-y-6" data-testid="dashboards-tiles">
            {showMy && (
                <section data-testid="my-dashboards-section">
                    <SectionHeader title="My dashboards" count={myTotal} testId="my-dashboards-section-header" />
                    {loading && !hasAnyDashboards ? (
                        <div className="py-6 text-center text-sm text-muted-foreground" data-testid="my-dashboards-loading">
                            <Loader2 className="h-5 w-5 mx-auto mb-1.5 animate-spin opacity-60" /> Loading…
                        </div>
                    ) : myRows.length === 0 ? (
                        <CompactEmpty testId="my-dashboards-empty" action={!hasAnyDashboards && onCreate ? (
                            <Button variant="outline" size="sm" className="h-8 rounded-md text-xs gap-1" onClick={onCreate} data-testid="my-dashboards-empty-create">
                                <Plus className="h-3.5 w-3.5" /> New dashboard
                            </Button>
                        ) : null}>
                            {hasAnyDashboards
                                ? "No dashboards match your filter."
                                : "You don't have any dashboards yet. Create one, or pick a prebuilt below."}
                        </CompactEmpty>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {myRows.map((d) => <MyDashboardCard key={d.id} d={d} ctx={ctx} />)}
                        </div>
                    )}
                    {myPager}
                </section>
            )}
            {showPrebuilt && (
                <section data-testid="prebuilt-dashboards-section">
                    <SectionHeader title="Prebuilt dashboards" count={prebuiltTotal} testId="prebuilt-dashboards-section-header" />
                    {prebuiltRows.length === 0 ? (
                        <CompactEmpty testId="templates-empty">
                            {loading ? "Loading prebuilt dashboards…" : "No prebuilt dashboards match your filter."}
                        </CompactEmpty>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {prebuiltRows.map((t) => <TemplateCard key={t.id} t={t} ctx={ctx} />)}
                        </div>
                    )}
                    {prebuiltPager}
                </section>
            )}
        </div>
    );
}
